// Takes a Batch Generation ID and creates a pdf with QR Codes
// and other info

//Take in a String (Batch ID) from the user
//Check if it's valid
//Get the data from that Batch
//Parse the data
//Lay it out as a pdf
//Download the PDF

import {
  getBatch,
  getPlantFromDatabase,
  getPrintJob,
  getSpeciesFromDatabase,
} from "../../DatabaseOperations";
import { useRef, useEffect, useState, useCallback, useMemo } from "react";
import { useLocation } from "react-router-dom";
import ConsoleHomePageHeader from "../Home/ConsoleHomePageHeader";

// Might want to switch to domtoimage
// https://stackoverflow.com/questions/22803825/html2canvas-generates-blurry-images
import html2canvas from "html2canvas";
import jsPDF from "jspdf";

import QRCodeLayout from "./QRCodeLayout";

import { averyTemplates } from "../../constants";

const exportSheetsAsPdf = async (elements, fileName) => {
  const sheets = elements.filter(Boolean);
  if (sheets.length === 0) return;

  const pdf = new jsPDF("portrait", "mm", "letter");
  const width = pdf.internal.pageSize.getWidth();
  const height = pdf.internal.pageSize.getHeight();

  for (let index = 0; index < sheets.length; index++) {
    const canvas = await html2canvas(sheets[index], {
      scale: 4,
      allowTaint: true,
      useCORS: true,
      letterRendering: true,
      backgroundColor: "#ffffff",
    });

    if (index > 0) {
      pdf.addPage("letter", "portrait");
    }

    const image = canvas.toDataURL("image/png", 1.0);
    pdf.addImage(image, "PNG", 0, 0, width, height);
  }

  pdf.save(`${fileName || "plant-tags"}.pdf`);
};

const buildPlaceholderItem = (key) => ({
  isPlaceholder: true,
  itemKey: `placeholder-${key}`,
});

const paginatePlants = (plants, pageSize) => {
  if (!plants.length || pageSize <= 0) return [];

  const pages = [];
  for (let i = 0; i < plants.length; i += pageSize) {
    const page = plants.slice(i, i + pageSize);
    while (page.length < pageSize) {
      page.push(buildPlaceholderItem(`${pages.length}-${page.length}`));
    }
    pages.push(page);
  }

  return pages;
};

const normalizeLoadedDocument = (documentID, rawData, kind) => ({
  documentID,
  documentType: kind,
  campaign: rawData.campaign || "",
  campaignLabel: rawData.campaignLabel || rawData.campaign || "",
  requestedItems:
    rawData.requestedItems ||
    (rawData.species
      ? [
          {
            species: rawData.species,
            count: rawData.count || (rawData.generatedPlants || []).length,
          },
        ]
      : []),
  generatedPlants: Array.isArray(rawData.generatedPlants) ? rawData.generatedPlants : [],
  totalCount:
    rawData.totalCount || rawData.count || (Array.isArray(rawData.generatedPlants) ? rawData.generatedPlants.length : 0),
  labelsPerSheet: rawData.labelsPerSheet || null,
  sheetCount: rawData.sheetCount || null,
  templateId: rawData.templateId || null,
});

const getMiddleStageIndex = (species) => {
  const stages = Array.isArray(species?.stages) ? species.stages : [];
  if (!stages.length) return 0;
  return Math.floor((stages.length - 1) / 2);
};

const TagGeneration = () => {
  const location = useLocation();
  const [plants, setPlants] = useState([]);
  const [lookupID, setLookupID] = useState("");
  const [selectedTemplate, setSelectedTemplate] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [loadedDocument, setLoadedDocument] = useState(null);
  const pageRefs = useRef([]);

  const hydratePlants = useCallback(async (items, campaign, campaignLabel, documentID, documentType) => {
    return Promise.all(
      items.map(async (item, index) => {
        if (!item?.plantID) {
          return buildPlaceholderItem(index);
        }

        let plant = null;
        let species = null;
        try {
          plant = await getPlantFromDatabase(item.plantID);
        } catch (_) {
          plant = null;
        }

        if (plant?.species) {
          try {
            species = await getSpeciesFromDatabase(plant.species);
          } catch (_) {
            species = null;
          }
        }

        if (plant) {
          plant = {
            ...plant,
            stage: getMiddleStageIndex(species),
          };
        }

        return {
          ...item,
          plant,
          species,
          plantType: item.plantType || item.species || plant?.species || "",
          campaign: item.campaign || campaign || "",
          campaignLabel: item.campaignLabel || campaignLabel || campaign || "",
          printJobId:
            item.printJobId || (documentType === "printJob" ? documentID || "" : ""),
          itemKey: `${item.plantID}-${item.secretCode || index}`,
        };
      })
    );
  }, []);

  const loadDocument = useCallback(
    async (documentID) => {
      if (!documentID || !documentID.trim()) {
        setError("Enter a print job ID or batch ID.");
        return;
      }

      setLoading(true);
      setError("");
      setPlants([]);
      setLoadedDocument(null);
      pageRefs.current = [];

      try {
        const printJobDoc = await getPrintJob(documentID.trim());
        let normalized = null;

        if (printJobDoc.exists() && printJobDoc.data()) {
          normalized = normalizeLoadedDocument(documentID.trim(), printJobDoc.data(), "printJob");
        } else {
          const batchDoc = await getBatch(documentID.trim());
          if (!batchDoc.exists() || !batchDoc.data()) {
            throw new Error("No print job or batch found with that ID.");
          }
          normalized = normalizeLoadedDocument(documentID.trim(), batchDoc.data(), "batch");
        }

        if (normalized.templateId) {
          const templateIndex = averyTemplates.findIndex(
            (template) => template.id === normalized.templateId
          );
          if (templateIndex >= 0) {
            setSelectedTemplate(templateIndex);
          }
        }

        const hydratedPlants = await hydratePlants(
          normalized.generatedPlants,
          normalized.campaign,
          normalized.campaignLabel,
          normalized.documentID,
          normalized.documentType
        );

        setLoadedDocument(normalized);
        setPlants(hydratedPlants);
      } catch (err) {
        console.error("Error loading printable document:", err);
        setError(err.message || "Failed to load tags.");
      } finally {
        setLoading(false);
      }
    },
    [hydratePlants]
  );

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const initialID = params.get("printJob") || params.get("batch") || params.get("id");
    if (!initialID) return;
    setLookupID(initialID);
    loadDocument(initialID);
  }, [location.search, loadDocument]);

  const handleSubmit = (e) => {
    e.preventDefault();
    loadDocument(lookupID);
  };

  const pages = useMemo(() => {
    const template = averyTemplates[selectedTemplate];
    const pageSize = template.rows * template.columns;
    return paginatePlants(plants, pageSize);
  }, [plants, selectedTemplate]);

  const setLabelFormat = (e) => {
    setSelectedTemplate(Number(e.target.value));
  };

  const downloadFileName = useMemo(() => {
    const base =
      loadedDocument?.campaign ||
      `${loadedDocument?.documentType || "tags"}-${loadedDocument?.documentID || "download"}`;
    return String(base).replace(/[^a-z0-9-_]+/gi, "-").replace(/^-+|-+$/g, "");
  }, [loadedDocument]);

  return (
    <div style={{ backgroundColor: "rgb(255, 240, 207)", minHeight: "100vh" }}>
      <ConsoleHomePageHeader />
      <div style={{ maxWidth: "960px", margin: "0 auto", padding: "20px" }}>
        <h2 style={{ marginTop: 0 }}>Tag Generation</h2>
        <form onSubmit={handleSubmit} style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
          <label style={{ flex: "1 1 300px" }}>
            Print Job ID or Batch ID:
            <input
              type="text"
              name="documentID"
              value={lookupID}
              onChange={(event) => setLookupID(event.target.value)}
              style={{ width: "100%", marginTop: 6, padding: "8px" }}
            />
          </label>
          <div style={{ alignSelf: "end" }}>
            <input type="submit" value={loading ? "Loading..." : "Load Tags"} disabled={loading} />
          </div>
        </form>

        <p style={{ marginBottom: 8 }}>Select label format:</p>
        <div>
          <label style={{ marginRight: 16 }}>
            <input type="radio" value="0" name="labelFormat" checked={selectedTemplate === 0} onChange={setLabelFormat} /> 1.25 x 2.25
          </label>
          <label>
            <input type="radio" value="1" name="labelFormat" checked={selectedTemplate === 1} onChange={setLabelFormat} /> 1.25 x 2
          </label>
        </div>

        {error ? (
          <div style={{ marginTop: 16, color: "#8b1e1e", backgroundColor: "#fde8e8", padding: 12, borderRadius: 6 }}>
            {error}
          </div>
        ) : null}

        {loadedDocument ? (
          <div style={{ marginTop: 20, backgroundColor: "#fff8ee", borderRadius: 10, padding: 16 }}>
            <div>
              <strong>Loaded:</strong> {loadedDocument.documentType} <code>{loadedDocument.documentID}</code>
            </div>
            <div style={{ marginTop: 8 }}>
              <strong>Total tags:</strong> {loadedDocument.totalCount} · <strong>Pages:</strong> {pages.length}
            </div>
            {loadedDocument.requestedItems.length ? (
              <div style={{ marginTop: 8 }}>
                <strong>Species mix:</strong>{" "}
                {loadedDocument.requestedItems
                  .map((item) => `${item.species} x ${item.count}`)
                  .join(", ")}
              </div>
            ) : null}
          </div>
        ) : null}

        {pages.length ? (
          <div style={{ marginTop: 20 }}>
            <button
              onClick={() =>
                exportSheetsAsPdf(
                  pages.map((_, index) => pageRefs.current[index]),
                  downloadFileName
                )
              }
              disabled={loading}
            >
              Download {pages.length}-page PDF
            </button>
            <div style={{ marginTop: 16, display: "grid", gap: 24 }}>
              {pages.map((pagePlants, index) => (
                <div key={`sheet-${index}`}>
                  <div style={{ marginBottom: 8, fontWeight: 600 }}>Sheet {index + 1}</div>
                  <QRCodeLayout
                    plants={pagePlants}
                    downloadRef={(node) => {
                      pageRefs.current[index] = node;
                    }}
                    selectedTemplate={selectedTemplate}
                  />
                </div>
              ))}
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default TagGeneration;
